import { view, i18next, cssGenerator, dialogs } from '@yola/ws-sdk';
import dialogTypes from 'src/js/modules/dialogs/constants/dialog-types';
import triggerIds from '../constants/line-and-paragraph-spacing-trigger-ids';
import lineHeightMap from '../constants/line-height-map';
import changeLineHeight from './change-line-height';
import getCustomLineHeight from './get-custom-line-height';

const { MARGIN_TOP, MARGIN_BOTTOM } = cssGenerator.constants.attributes;

const DEFAULT_MARGIN = '24';
const LINE_HEIGHT_AUTO = 'auto';

const getCurrentLineHeight = (node, breakpoint) =>
  getCustomLineHeight(node, breakpoint) ?? LINE_HEIGHT_AUTO;

const createItemHandler = (focusedElements, breakpoint) => (id) => () => {
  focusedElements.forEach((node) => {
    switch (id) {
      case triggerIds.ADD_SPACE_BEFORE: {
        const marginTop = node.getAttribute(MARGIN_TOP);
        if (parseInt(marginTop, 10)) {
          break;
        }
        if (node.previousElementSibling) {
          node.removeAttribute(MARGIN_TOP);
        } else {
          node.setAttribute(MARGIN_TOP, DEFAULT_MARGIN);
        }
        break;
      }
      case triggerIds.REMOVE_SPACE_BEFORE:
        node.setAttribute(MARGIN_TOP, '0');
        break;
      case triggerIds.ADD_SPACE_AFTER:
        node.setAttribute(MARGIN_BOTTOM, DEFAULT_MARGIN);
        break;
      case triggerIds.REMOVE_SPACE_AFTER:
        node.removeAttribute(MARGIN_BOTTOM);
        break;
      case triggerIds.LINE_HEIGHT_AUTO:
        changeLineHeight({ value: null, focusedElements: [node], breakpoint });
        break;
      case triggerIds.LINE_HEIGHT_1:
      case triggerIds.LINE_HEIGHT_1_1:
      case triggerIds.LINE_HEIGHT_1_2:
      case triggerIds.LINE_HEIGHT_1_3:
      case triggerIds.LINE_HEIGHT_1_5:
      case triggerIds.LINE_HEIGHT_1_6:
      case triggerIds.LINE_HEIGHT_1_7:
        changeLineHeight({
          value: lineHeightMap[id],
          focusedElements: [node],
          breakpoint,
        });
        break;
      case triggerIds.CUSTOM_SPACING: {
        dialogs.operations.show(dialogTypes.TEXT_CUSTOM_SPACING_DIALOG, {
          focusedElements,
        });
        break;
      }
      default:
        break;
    }
  });
};

const getLineAndParagraphSpacingMenuGroups = (element, focusedElements, breakpoint) => {
  const liveDocument = view.accessors.getLiveDocument();

  let canRemoveSpaceBefore = true;
  let canRemoveSpaceAfter = true;
  const lineHeights = [];

  // eslint-disable-next-line no-restricted-syntax
  for (const node of focusedElements) {
    const { marginTop, marginBottom } = liveDocument.defaultView.getComputedStyle(node);
    const mt = parseFloat(marginTop) || 0;
    const mb = parseFloat(marginBottom) || 0;

    if (mt === 0) canRemoveSpaceBefore = false;
    if (mb === 0) canRemoveSpaceAfter = false;

    lineHeights.push(getCurrentLineHeight(node, breakpoint));
  }

  const activeLineHeightItem = lineHeights.every((lh) => lh === lineHeights[0])
    ? lineHeights[0]
    : '';

  const getItemHandler = createItemHandler(focusedElements, breakpoint);

  const defaultLineHeights = [
    lineHeightMap[triggerIds.LINE_HEIGHT_1],
    lineHeightMap[triggerIds.LINE_HEIGHT_1_1],
    lineHeightMap[triggerIds.LINE_HEIGHT_1_2],
    lineHeightMap[triggerIds.LINE_HEIGHT_1_3],
    lineHeightMap[triggerIds.LINE_HEIGHT_1_5],
    lineHeightMap[triggerIds.LINE_HEIGHT_1_6],
    lineHeightMap[triggerIds.LINE_HEIGHT_1_7],
  ];

  const isCustomLineHeight =
    activeLineHeightItem !== LINE_HEIGHT_AUTO &&
    !defaultLineHeights.some((height) => activeLineHeightItem.includes(height));

  return [
    {
      name: 'line-height',
      element,
      items: [
        {
          id: triggerIds.LINE_HEIGHT_AUTO,
          caption: i18next.t('Auto'),
          active: activeLineHeightItem === LINE_HEIGHT_AUTO,
          onClick: getItemHandler(triggerIds.LINE_HEIGHT_AUTO),
        },
        {
          id: triggerIds.LINE_HEIGHT_1,
          caption: '1',
          active: activeLineHeightItem.includes(lineHeightMap[triggerIds.LINE_HEIGHT_1]),
          onClick: getItemHandler(triggerIds.LINE_HEIGHT_1),
        },
        {
          id: triggerIds.LINE_HEIGHT_1_1,
          caption: '1.1',
          active: activeLineHeightItem.includes(lineHeightMap[triggerIds.LINE_HEIGHT_1_1]),
          onClick: getItemHandler(triggerIds.LINE_HEIGHT_1_1),
        },
        {
          id: triggerIds.LINE_HEIGHT_1_2,
          caption: '1.2',
          active: activeLineHeightItem.includes(lineHeightMap[triggerIds.LINE_HEIGHT_1_2]),
          onClick: getItemHandler(triggerIds.LINE_HEIGHT_1_2),
        },
        {
          id: triggerIds.LINE_HEIGHT_1_3,
          caption: '1.3',
          active: activeLineHeightItem.includes(lineHeightMap[triggerIds.LINE_HEIGHT_1_3]),
          onClick: getItemHandler(triggerIds.LINE_HEIGHT_1_3),
        },
        {
          id: triggerIds.LINE_HEIGHT_1_5,
          caption: '1.5',
          active: activeLineHeightItem.includes(lineHeightMap[triggerIds.LINE_HEIGHT_1_5]),
          onClick: getItemHandler(triggerIds.LINE_HEIGHT_1_5),
        },
        {
          id: triggerIds.LINE_HEIGHT_1_6,
          caption: '1.6',
          active: activeLineHeightItem.includes(lineHeightMap[triggerIds.LINE_HEIGHT_1_6]),
          onClick: getItemHandler(triggerIds.LINE_HEIGHT_1_6),
        },
        {
          id: triggerIds.LINE_HEIGHT_1_7,
          caption: '1.7',
          active: activeLineHeightItem.includes(lineHeightMap[triggerIds.LINE_HEIGHT_1_7]),
          onClick: getItemHandler(triggerIds.LINE_HEIGHT_1_7),
        },
      ],
    },
    {
      name: 'paragraph-spacing',
      element,
      items: [
        canRemoveSpaceBefore
          ? {
              id: triggerIds.REMOVE_SPACE_BEFORE,
              caption: i18next.t('Remove space before paragraph'),
              onClick: getItemHandler(triggerIds.REMOVE_SPACE_BEFORE),
            }
          : {
              id: triggerIds.ADD_SPACE_BEFORE,
              caption: i18next.t('Add space before paragraph'),
              onClick: getItemHandler(triggerIds.ADD_SPACE_BEFORE),
            },
        canRemoveSpaceAfter
          ? {
              id: triggerIds.REMOVE_SPACE_AFTER,
              caption: i18next.t('Remove space after paragraph'),
              onClick: getItemHandler(triggerIds.REMOVE_SPACE_AFTER),
            }
          : {
              id: triggerIds.ADD_SPACE_AFTER,
              caption: i18next.t('Add space after paragraph'),
              onClick: getItemHandler(triggerIds.ADD_SPACE_AFTER),
            },
      ],
    },
    {
      name: 'custom-spacing',
      element,
      items: [
        {
          id: triggerIds.CUSTOM_SPACING,
          caption: `${i18next.t('Custom')}...`,
          active: isCustomLineHeight,
          onClick: getItemHandler(triggerIds.CUSTOM_SPACING),
        },
      ],
    },
  ];
};

export default getLineAndParagraphSpacingMenuGroups;
